import {
  Form,
  MonetaryInputField,
  SelectField,
  SubmitButton,
} from '@/components';
import { UpsertPriorExecutionItemFormSchema } from '@/features/project/projects/_models/execution-item';
import type { ExpenditureCartsModel } from '@/features/project/projects/_models/expenditure-cart';
import { useForm } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type FormValues = z.infer<typeof UpsertPriorExecutionItemFormSchema>;

type Props = {
  expenditureCarts: z.infer<typeof ExpenditureCartsModel>;
  onSubmit: (values: FormValues) => void;
};

export const UpsertPriorExecutionItemForm: FC<Props> = ({
  expenditureCarts,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
    watch,
    setValue,
  } = useForm<FormValues>({
    resolver: zodResolver(UpsertPriorExecutionItemFormSchema),
  });

  const expenditureCartId = watch('expenditureCartId');

  useEffect(() => {
    // NOTES: subProject should exist when this component is called.
    const expenditureCart = expenditureCarts.find(
      (cart) => cart.id === expenditureCartId,
    );
    const amount = expenditureCart?.executionItem?.amount;
    if (typeof amount === 'number') {
      setValue('amount', amount);
    }
  }, [expenditureCartId, expenditureCarts, setValue]);

  const expenditureCartOptions = expenditureCarts.map((cart) => ({
    value: cart.id,
    label: cart.accountTitle
      ? `${cart.accountingType.name} - ${cart.accountTitle.name}`
      : cart.accountingType.name,
  }));

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <SelectField
        name="expenditureCartId"
        label={t('expenditure-cart')}
        options={expenditureCartOptions}
        control={control}
        errors={errors}
        disabled={!expenditureCartOptions}
        required
      />
      <MonetaryInputField
        name="amount"
        label={t('amount')}
        hint={t('hint-upsert-prior-execution-item')}
        control={control}
        errors={errors}
        unit={unit}
      />
      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
